import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ShoppingCart, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatCurrency } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const EmptyFeedBags = () => {
  const { currencySymbol } = useCurrency();
  const [dailyBalance, setDailyBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState([]);
  const [sales, setSales] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [saleOpen, setSaleOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isDistributor = user.role === 'distributor';
  const isCustomer = user.role === 'customer';
  const isSimplifiedView = isDistributor || isCustomer;  // Both get simplified view

  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    layer_mash_dozens: '',
    layer_mash_singles: '',
    starter_dozens: '',
    starter_singles: '',
    grower_mash_dozens: '',
    grower_mash_singles: '',
    notes: ''
  });

  const [saleForm, setSaleForm] = useState({
    date: new Date().toISOString().split('T')[0],
    dozens_sold: '',
    price_per_dozen: '',
    buyer_name: '',
    notes: ''
  });

  useEffect(() => {
    fetchDailyBalance(selectedDate);
    // Distributors and Customers only need the balance, not the detailed entries/sales
    if (!isSimplifiedView) {
      fetchEntries();
      fetchSales();
    }
  }, [selectedDate, isSimplifiedView]);

  const fetchDailyBalance = async (date) => {
    try {
      // Use the Chicken Feed Audit endpoint for empty bag summary
      const response = await axios.get(`${API}/empty-feed-bags/summary/${date}`, getAuthHeader());
      // Transform the response to match expected format
      const summary = response.data;
      setDailyBalance({
        date: date,
        closing_balance_dozens: summary.inventory?.dozens || 0,
        closing_balance_singles: summary.inventory?.singles || 0,
        closing_balance_total: summary.inventory?.closing || 0
      });
    } catch (error) {
      console.error('Failed to fetch daily balance');
      // If endpoint fails, set to 0
      setDailyBalance({
        date: date,
        closing_balance_dozens: 0,
        closing_balance_singles: 0,
        closing_balance_total: 0
      });
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/empty-bags/entries`, getAuthHeader());
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries');
    }
  };

  const fetchSales = async () => {
    try {
      const response = await axios.get(`${API}/empty-bags/sales`, getAuthHeader());
      setSales(response.data);
    } catch (error) {
      console.error('Failed to fetch sales');
    }
  };

  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...entryForm,
        layer_mash_dozens: parseInt(entryForm.layer_mash_dozens) || 0,
        layer_mash_singles: parseInt(entryForm.layer_mash_singles) || 0,
        starter_dozens: parseInt(entryForm.starter_dozens) || 0,
        starter_singles: parseInt(entryForm.starter_singles) || 0,
        grower_mash_dozens: parseInt(entryForm.grower_mash_dozens) || 0,
        grower_mash_singles: parseInt(entryForm.grower_mash_singles) || 0
      };

      if (editingEntry) {
        await axios.put(`${API}/empty-bags/entries/${editingEntry.id}`, payload, getAuthHeader());
        toast.success('Entry updated successfully');
      } else {
        await axios.post(`${API}/empty-bags/entries`, payload, getAuthHeader());
        toast.success('Empty bag entry added');
      }
      
      fetchDailyBalance(selectedDate);
      fetchEntries();
      handleCloseEntryDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...saleForm,
        dozens_sold: parseInt(saleForm.dozens_sold) || 0,
        price_per_dozen: parseFloat(saleForm.price_per_dozen) || 0
      };

      if (editingSale) {
        await axios.put(`${API}/empty-bags/sales/${editingSale.id}`, payload, getAuthHeader());
        toast.success('Sale updated successfully');
      } else {
        await axios.post(`${API}/empty-bags/sales`, payload, getAuthHeader());
        toast.success('Empty bag sale recorded');
      }
      
      fetchDailyBalance(selectedDate);
      fetchSales();
      handleCloseSaleDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sale');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date,
      layer_mash_dozens: entry.layer_mash_dozens?.toString() || '',
      layer_mash_singles: entry.layer_mash_singles?.toString() || '',
      starter_dozens: entry.starter_dozens?.toString() || '',
      starter_singles: entry.starter_singles?.toString() || '',
      grower_mash_dozens: entry.grower_mash_dozens?.toString() || '',
      grower_mash_singles: entry.grower_mash_singles?.toString() || '',
      notes: entry.notes || ''
    });
    setEntryOpen(true);
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setSaleForm({
      date: sale.date,
      dozens_sold: sale.dozens_sold?.toString() || '',
      price_per_dozen: sale.price_per_dozen?.toString() || '',
      buyer_name: sale.buyer_name || '',
      notes: sale.notes || ''
    });
    setSaleOpen(true);
  };

  const handleCloseEntryDialog = () => {
    setEntryOpen(false);
    setEditingEntry(null);
    setEntryForm({
      date: selectedDate,
      layer_mash_dozens: '',
      layer_mash_singles: '',
      starter_dozens: '',
      starter_singles: '',
      grower_mash_dozens: '',
      grower_mash_singles: '',
      notes: ''
    });
  };

  const handleCloseSaleDialog = () => {
    setSaleOpen(false);
    setEditingSale(null);
    setSaleForm({
      date: selectedDate,
      dozens_sold: '',
      price_per_dozen: '',
      buyer_name: '',
      notes: ''
    });
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/empty-bags/entries/${id}`, getAuthHeader());
      toast.success('Entry deleted');
      fetchDailyBalance(selectedDate);
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleDeleteSale = async (id) => {
    if (!window.confirm('Delete this sale?')) return;
    try {
      await axios.delete(`${API}/empty-bags/sales/${id}`, getAuthHeader());
      toast.success('Sale deleted');
      fetchDailyBalance(selectedDate);
      fetchSales();
    } catch (error) {
      toast.error('Failed to delete sale');
    }
  };

  // Simplified view for Distributors and Customers - only show closing balance
  if (isSimplifiedView) {
    return (
      <div className="space-y-6" data-testid="empty-feed-bags-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Empty Feed Bags
          </h1>
          <p className="text-lg text-slate-600 mt-2">Available empty bags balance</p>
        </div>

        {dailyBalance ? (
          <div className="max-w-lg">
            <div className="card p-8 bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
              <div className="text-center">
                <p className="text-sm font-medium text-purple-600 uppercase tracking-wider mb-2">
                  Current Available Balance
                </p>
                <p className="text-lg text-slate-500 mb-4">
                  As of {formatDate(new Date().toISOString().split('T')[0])}
                </p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-purple-100 rounded-xl p-6">
                    <p className="text-6xl font-bold text-purple-700" data-testid="closing-balance-dozens">
                      {dailyBalance.closing_balance_dozens}
                    </p>
                    <p className="text-xl text-purple-600 mt-2">dozens</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-6">
                    <p className="text-6xl font-bold text-purple-500" data-testid="closing-balance-singles">
                      {dailyBalance.closing_balance_singles}
                    </p>
                    <p className="text-xl text-purple-400 mt-2">singles</p>
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  Empty feed bags available for sale (from Chicken Feed Audit)
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card p-8 text-center text-slate-500">
            Loading balance...
          </div>
        )}

        {isCustomer && (
          <p className="text-sm text-slate-600 italic mt-4">
            Please contact your Distributor for details on how to purchase empty feed bags.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="empty-feed-bags-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Empty Feed Bags
          </h1>
          <p className="text-lg text-slate-600 mt-2">Track empty feed bags inventory and sales</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-entry-button" onClick={() => setEntryOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Bags Used
          </Button>
          <Button data-testid="add-sale-button" onClick={() => setSaleOpen(true)} variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Record Sale
          </Button>
        </div>
      </div>

      {/* Date Selector */}
      <div className="flex items-center gap-4">
        <Label>View Date:</Label>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-48"
            data-testid="date-selector"
          />
          <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-2 rounded">
            {formatDate(selectedDate)}
          </span>
        </div>
      </div>

      {/* Daily Balance Summary */}
      {dailyBalance && (
        <div className="card p-6">
          <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Empty Bag Balance - {formatDate(selectedDate)}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500">Opening Balance</p>
              <p className="text-2xl font-bold text-slate-900">{dailyBalance.opening_balance_dozens} dozens</p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg">
              <p className="text-sm text-emerald-600">+ Bags Added Today</p>
              <p className="text-2xl font-bold text-emerald-700">{dailyBalance.today_entries.converted_dozens} dozens</p>
              <p className="text-xs text-slate-500">({dailyBalance.today_entries.remaining_singles} singles remaining)</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-sm text-red-600">- Bags Sold Today</p>
              <p className="text-2xl font-bold text-red-700">{dailyBalance.today_sales_dozens} dozens</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-600">Closing Balance</p>
              <p className="text-2xl font-bold text-purple-700">{dailyBalance.closing_balance_dozens} dozens</p>
            </div>
          </div>

          {/* Breakdown by Feed Type */}
          <h4 className="text-lg font-medium mb-3">Today's Bags by Feed Type</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3">Feed Type</th>
                  <th className="text-center py-2 px-3">Dozens</th>
                  <th className="text-center py-2 px-3">Singles</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-3">Layer Mash</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.layer_mash.dozens}</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.layer_mash.singles}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-3">Starter</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.starter.dozens}</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.starter.singles}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-3">Grower Mash</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.grower_mash.dozens}</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.grower_mash.singles}</td>
                </tr>
                <tr className="bg-slate-50 font-semibold">
                  <td className="py-2 px-3">Total</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.total_dozens}</td>
                  <td className="text-center py-2 px-3">{dailyBalance.today_entries.total_singles}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabs for Entries and Sales */}
      <Tabs defaultValue="entries" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="entries" data-testid="entries-tab">Bags Used (from Pens)</TabsTrigger>
          <TabsTrigger value="sales" data-testid="sales-tab">Bags Sold</TabsTrigger>
        </TabsList>

        <TabsContent value="entries">
          <div className="card">
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-center">Layer Mash (D/S)</th>
                    <th className="text-center">Starter (D/S)</th>
                    <th className="text-center">Grower Mash (D/S)</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="entries-table-body">
                  {entries.slice(0, 20).map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.date)}</td>
                      <td className="text-center">{entry.layer_mash_dozens} / {entry.layer_mash_singles}</td>
                      <td className="text-center">{entry.starter_dozens} / {entry.starter_singles}</td>
                      <td className="text-center">{entry.grower_mash_dozens} / {entry.grower_mash_singles}</td>
                      <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                      <td>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditEntry(entry)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit entry"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete entry"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {entries.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No empty bag entries yet.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sales">
          <div className="card">
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-center">Dozens Sold</th>
                    <th className="text-right">Price/Dozen</th>
                    <th className="text-right">Total</th>
                    <th>Buyer</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="sales-table-body">
                  {sales.slice(0, 20).map((sale) => (
                    <tr key={sale.id}>
                      <td>{formatDate(sale.date)}</td>
                      <td className="text-center font-semibold text-red-600">-{sale.dozens_sold}</td>
                      <td className="text-right">{currencySymbol}{formatCurrency(sale.price_per_dozen || 0)}</td>
                      <td className="text-right font-semibold text-emerald-600">{currencySymbol}{formatCurrency(sale.total_amount || 0)}</td>
                      <td>{sale.buyer_name || '-'}</td>
                      <td className="max-w-xs truncate">{sale.notes || '-'}</td>
                      <td>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditSale(sale)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit sale"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSale(sale.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete sale"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sales.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No empty bag sales yet.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Entry Dialog */}
      <Dialog open={entryOpen} onOpenChange={(open) => !open && handleCloseEntryDialog()}>
        <DialogContent data-testid="entry-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Empty Bags Entry' : 'Add Empty Bags Used at Pens'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEntrySubmit} className="space-y-4">
            <div>
              <Label htmlFor="entry-date">Date</Label>
              <Input
                id="entry-date"
                type="date"
                value={entryForm.date}
                onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Layer Mash - Dozens</Label>
                <Input
                  type="number"
                  value={entryForm.layer_mash_dozens}
                  onChange={(e) => setEntryForm({ ...entryForm, layer_mash_dozens: e.target.value })}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <Label>Layer Mash - Singles</Label>
                <Input
                  type="number"
                  value={entryForm.layer_mash_singles}
                  onChange={(e) => setEntryForm({ ...entryForm, layer_mash_singles: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="11"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Starter - Dozens</Label>
                <Input
                  type="number"
                  value={entryForm.starter_dozens}
                  onChange={(e) => setEntryForm({ ...entryForm, starter_dozens: e.target.value })}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <Label>Starter - Singles</Label>
                <Input
                  type="number"
                  value={entryForm.starter_singles}
                  onChange={(e) => setEntryForm({ ...entryForm, starter_singles: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="11"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grower Mash - Dozens</Label>
                <Input
                  type="number"
                  value={entryForm.grower_mash_dozens}
                  onChange={(e) => setEntryForm({ ...entryForm, grower_mash_dozens: e.target.value })}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <Label>Grower Mash - Singles</Label>
                <Input
                  type="number"
                  value={entryForm.grower_mash_singles}
                  onChange={(e) => setEntryForm({ ...entryForm, grower_mash_singles: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="entry-notes">Notes (Optional - e.g., damages, wastage)</Label>
              <Textarea
                id="entry-notes"
                value={entryForm.notes}
                onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
                placeholder="Any notes about the bags (damages, wastage, etc.)..."
              />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCloseEntryDialog}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record/Edit Sale Dialog */}
      <Dialog open={saleOpen} onOpenChange={(open) => !open && handleCloseSaleDialog()}>
        <DialogContent data-testid="sale-dialog">
          <DialogHeader>
            <DialogTitle>{editingSale ? 'Edit Empty Bags Sale' : 'Record Empty Bags Sale'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="sale-date">Date</Label>
              <Input
                id="sale-date"
                type="date"
                value={saleForm.date}
                onChange={(e) => setSaleForm({ ...saleForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="dozens-sold">Dozens Sold</Label>
              <Input
                id="dozens-sold"
                type="number"
                value={saleForm.dozens_sold}
                onChange={(e) => setSaleForm({ ...saleForm, dozens_sold: e.target.value })}
                min="1"
                required
              />
            </div>
            <div>
              <Label htmlFor="price-per-dozen">Price per Dozen</Label>
              <Input
                id="price-per-dozen"
                type="number"
                step="0.01"
                value={saleForm.price_per_dozen}
                onChange={(e) => setSaleForm({ ...saleForm, price_per_dozen: e.target.value })}
                min="0"
                required
              />
            </div>
            <div>
              <Label htmlFor="buyer-name">Buyer Name (Optional)</Label>
              <Input
                id="buyer-name"
                value={saleForm.buyer_name}
                onChange={(e) => setSaleForm({ ...saleForm, buyer_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sale-notes">Notes (Optional)</Label>
              <Textarea
                id="sale-notes"
                value={saleForm.notes}
                onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                {editingSale ? 'Update Sale' : 'Record Sale'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCloseSaleDialog}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmptyFeedBags;
